import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/**
 * Portfolio console (Wave 4 M-02): every workspace the user belongs to —
 * an advisor's one-screen view of client plan, role, headline emissions
 * and disclosure readiness, with a direct switch into any client.
 */
interface PortfolioCompany {
	id: number;
	name: string;
	role: string;
	plan: string;
	subscriptionStatus: string | null;
	scope12Tonnes: number;
	scope3Tonnes: number;
	narrativeSections: number;
	targets: number;
	risks: number;
	scenarios: number;
}

const planLabel = (p: string) =>
	p === "measure"
		? "Premium"
		: p === "enable"
			? "Executive"
			: p.charAt(0).toUpperCase() + p.slice(1);

const Portfolio: React.FC = () => {
	const { user, getAccessTokenSilently } = useAuth0();
	const thisYear = new Date().getFullYear();
	const [year, setYear] = useState(thisYear - 1);
	const [companies, setCompanies] = useState<PortfolioCompany[]>([]);
	const [loading, setLoading] = useState(true);
	const [switching, setSwitching] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const activeCompanyId = String(
		(user?.["https://arteh.com.au/user_metadata"] as any)?.activeCompanyId ??
			"",
	);

	const authFetch = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			return fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
		},
		[getAccessTokenSilently],
	);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			setError(null);
			try {
				const res = await authFetch(
					`/api/portfolio/overview?year=${year}`,
				);
				const json = await res.json().catch(() => ({}));
				if (!res.ok || json.Ok === false)
					throw new Error(
						json.Message || "Could not load the portfolio",
					);
				if (!cancelled)
					setCompanies((json.Data || json).companies || []);
			} catch (e: any) {
				if (!cancelled)
					setError(e.message || "Could not load the portfolio");
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [authFetch, year]);

	const switchTo = async (companyId: number) => {
		setSwitching(companyId);
		try {
			const res = await authFetch("/api/user/switch-company", {
				method: "POST",
				body: JSON.stringify({ companyId }),
			});
			if (!res.ok) throw new Error("switch failed");
			await getAccessTokenSilently({ cacheMode: "off" } as any);
			window.location.assign("/dashboard");
		} catch {
			setError("Could not switch to that company.");
			setSwitching(null);
		}
	};

	const years = Array.from({ length: 6 }, (_, i) => thisYear - i);

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={2} sx={{ p: 4 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
						flexWrap: "wrap",
						mb: 2,
					}}
				>
					<Box sx={{ flex: 1, minWidth: 260 }}>
						<Typography variant="h5">Portfolio</Typography>
						<Typography variant="body2" color="text.secondary">
							Every workspace you belong to: plan, your role,
							headline emissions and disclosure readiness for the
							selected year. Switch straight into any client.
						</Typography>
					</Box>
					<FormControl size="small" sx={{ minWidth: 130 }}>
						<InputLabel id="pf-year">Year</InputLabel>
						<Select
							labelId="pf-year"
							label="Year"
							value={year}
							onChange={(e) => setYear(Number(e.target.value))}
						>
							{years.map((y) => (
								<MenuItem key={y} value={y}>
									{y}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>

				{error && (
					<Alert
						severity="error"
						sx={{ mb: 2 }}
						onClose={() => setError(null)}
					>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ textAlign: "center", py: 6 }}>
						<CircularProgress />
					</Box>
				) : companies.length === 0 ? (
					<Typography
						color="text.secondary"
						sx={{ py: 4, textAlign: "center" }}
					>
						No workspaces found for this account.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Company</TableCell>
								<TableCell>Your role</TableCell>
								<TableCell>Plan</TableCell>
								<TableCell align="right">Scope 1+2 t</TableCell>
								<TableCell align="right">Scope 3 t</TableCell>
								<TableCell align="right">
									Disclosure readiness
								</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{companies.map((c) => (
								<TableRow
									key={c.id}
									selected={String(c.id) === activeCompanyId}
								>
									<TableCell>
										<strong>{c.name}</strong>
										{String(c.id) === activeCompanyId && (
											<Chip
												size="small"
												label="Active"
												color="primary"
												sx={{ ml: 1, height: 18 }}
											/>
										)}
									</TableCell>
									<TableCell
										sx={{ textTransform: "capitalize" }}
									>
										{c.role === "assurer" ? (
											<Chip
												size="small"
												label="Assurer (read-only)"
											/>
										) : (
											c.role
										)}
									</TableCell>
									<TableCell>{planLabel(c.plan)}</TableCell>
									<TableCell align="right">
										{c.scope12Tonnes.toLocaleString()}
									</TableCell>
									<TableCell align="right">
										{c.scope3Tonnes.toLocaleString()}
									</TableCell>
									<TableCell align="right">
										<Typography variant="caption">
											{c.narrativeSections}/4 narratives ·{" "}
											{c.targets} target
											{c.targets === 1 ? "" : "s"} ·{" "}
											{c.risks} risk
											{c.risks === 1 ? "" : "s"} ·{" "}
											{c.scenarios} scenario
											{c.scenarios === 1 ? "" : "s"}
										</Typography>
									</TableCell>
									<TableCell align="right">
										{String(c.id) !== activeCompanyId && (
											<Button
												size="small"
												startIcon={
													switching === c.id ? (
														<CircularProgress
															size={14}
														/>
													) : (
														<SwapHorizIcon />
													)
												}
												onClick={() => switchTo(c.id)}
												disabled={switching !== null}
											>
												Open
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>
		</Container>
	);
};

export default Portfolio;
